'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Save, X, Plus, Trash2 } from 'lucide-react'
import type { WikipediaResult } from '@creanizer/types'

interface WikipediaEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: Partial<WikipediaResult>) => void
  wikipediaResult: WikipediaResult
  title?: string
}

export function WikipediaEditModal({
  isOpen,
  onClose,
  onSave,
  wikipediaResult,
  title = '架空事典を編集'
}: WikipediaEditModalProps) {
  // 基本情報
  const [editedTitle, setEditedTitle] = useState(wikipediaResult.title)
  const [editedCategory, setEditedCategory] = useState(wikipediaResult.category)
  const [editedLeadSection, setEditedLeadSection] = useState(wikipediaResult.leadSection)
  
  // インフォボックス
  const [editedInfobox, setEditedInfobox] = useState(wikipediaResult.infobox || {})
  const [newInfoboxKey, setNewInfoboxKey] = useState('')
  const [newInfoboxValue, setNewInfoboxValue] = useState('')
  
  // カテゴリ
  const [editedCategories, setEditedCategories] = useState(wikipediaResult.categories || [])
  const [newCategory, setNewCategory] = useState('')
  
  // 関連項目
  const [editedSeeAlso, setEditedSeeAlso] = useState(wikipediaResult.seeAlso || [])
  const [newSeeAlso, setNewSeeAlso] = useState('')
  
  // 参考文献
  const [editedReferences, setEditedReferences] = useState(wikipediaResult.references || [])
  const [newReference, setNewReference] = useState('')

  // wikipediaResultが変更されたら編集中の値をリセット
  useEffect(() => {
    setEditedTitle(wikipediaResult.title)
    setEditedCategory(wikipediaResult.category)
    setEditedLeadSection(wikipediaResult.leadSection)
    setEditedInfobox(wikipediaResult.infobox || {})
    setEditedCategories(wikipediaResult.categories || [])
    setEditedSeeAlso(wikipediaResult.seeAlso || [])
    setEditedReferences(wikipediaResult.references || [])
  }, [wikipediaResult])
  
  const handleAddInfoboxItem = () => {
    if (newInfoboxKey && newInfoboxValue) {
      setEditedInfobox({
        ...editedInfobox,
        [newInfoboxKey]: newInfoboxValue
      })
      setNewInfoboxKey('')
      setNewInfoboxValue('')
    }
  }
  
  const handleRemoveInfoboxItem = (key: string) => {
    const updated = { ...editedInfobox }
    delete updated[key]
    setEditedInfobox(updated)
  }
  
  const handleAddCategory = () => {
    if (newCategory && !editedCategories.includes(newCategory)) {
      setEditedCategories([...editedCategories, newCategory])
      setNewCategory('')
    }
  }
  
  const handleRemoveCategory = (category: string) => {
    setEditedCategories(editedCategories.filter(c => c !== category))
  }
  
  const handleAddSeeAlso = () => {
    if (newSeeAlso && !editedSeeAlso.includes(newSeeAlso)) {
      setEditedSeeAlso([...editedSeeAlso, newSeeAlso])
      setNewSeeAlso('')
    }
  }
  
  const handleRemoveSeeAlso = (item: string) => {
    setEditedSeeAlso(editedSeeAlso.filter(s => s !== item))
  }
  
  const handleAddReference = () => {
    if (newReference) {
      setEditedReferences([...editedReferences, newReference])
      setNewReference('')
    }
  }
  
  const handleRemoveReference = (index: number) => {
    setEditedReferences(editedReferences.filter((_, i) => i !== index))
  }
  
  const handleSave = () => {
    onSave({
      title: editedTitle,
      category: editedCategory,
      leadSection: editedLeadSection,
      infobox: editedInfobox,
      categories: editedCategories,
      seeAlso: editedSeeAlso,
      references: editedReferences,
      // sections と tableOfContents は変更しない（複雑すぎるため）
      sections: wikipediaResult.sections,
      tableOfContents: wikipediaResult.tableOfContents
    })
    onClose()
  }
  
  const handleCancel = () => {
    // 編集内容をリセット
    setEditedTitle(wikipediaResult.title)
    setEditedCategory(wikipediaResult.category)
    setEditedLeadSection(wikipediaResult.leadSection)
    setEditedInfobox(wikipediaResult.infobox || {})
    setEditedCategories(wikipediaResult.categories || [])
    setEditedSeeAlso(wikipediaResult.seeAlso || [])
    setEditedReferences(wikipediaResult.references || [])
    onClose()
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* 基本情報 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">基本情報</h3>
            
            <div>
              <Label htmlFor="title">項目名</Label>
              <Input
                id="title"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                placeholder="項目の名前"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="category">カテゴリ</Label>
              <Input
                id="category"
                value={editedCategory}
                onChange={(e) => setEditedCategory(e.target.value)}
                placeholder="メインカテゴリ"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="leadSection">概要</Label>
              <Textarea
                id="leadSection"
                value={editedLeadSection}
                onChange={(e) => setEditedLeadSection(e.target.value)}
                placeholder="記事の概要"
                rows={5}
                className="mt-1"
              />
            </div>
          </div>
          
          {/* インフォボックス */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">基本情報ボックス</h3>
            
            {Object.entries(editedInfobox).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <Input value={key} disabled className="flex-1" />
                <Input value={value} disabled className="flex-1" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRemoveInfoboxItem(key)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            <div className="flex items-center gap-2">
              <Input
                value={newInfoboxKey}
                onChange={(e) => setNewInfoboxKey(e.target.value)}
                placeholder="項目名"
                className="flex-1"
              />
              <Input
                value={newInfoboxValue}
                onChange={(e) => setNewInfoboxValue(e.target.value)}
                placeholder="値"
                className="flex-1"
              />
              <Button onClick={handleAddInfoboxItem} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* カテゴリ */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">カテゴリ</h3>
            
            <div className="flex flex-wrap gap-2">
              {editedCategories.map((category, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => handleRemoveCategory(category)}
                >
                  {category} ×
                </Badge>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="新しいカテゴリを追加"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddCategory()
                  }
                }}
              />
              <Button onClick={handleAddCategory} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* 関連項目 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">関連項目</h3>
            
            <div className="space-y-2">
              {editedSeeAlso.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input value={item} disabled className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemoveSeeAlso(item)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newSeeAlso}
                onChange={(e) => setNewSeeAlso(e.target.value)}
                placeholder="新しい関連項目を追加"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddSeeAlso()
                  }
                }}
              />
              <Button onClick={handleAddSeeAlso} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* 参考文献 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">参考文献</h3>
            
            <div className="space-y-2">
              {editedReferences.map((ref, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">[{index + 1}]</span>
                  <Input value={ref} disabled className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemoveReference(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newReference}
                onChange={(e) => setNewReference(e.target.value)}
                placeholder="新しい参考文献を追加"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddReference()
                  }
                }}
              />
              <Button onClick={handleAddReference} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}